/* global SideDrawer, theme */

class CartDrawer extends SideDrawer {
  connectedCallback() {
    this.bindEvents();
  }

  bindEvents() {
    this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
    this.closeDrawerViaEventHandler = this.close.bind(this, null);
    this.completeLookClickHandler = this.handleCompleteLookClick.bind(this);
    document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    this.addEventListener('click', this.completeLookClickHandler);
    this.addEventListener('on:cart-drawer:before-open', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
    });
    this.addEventListener('on:cart:after-merge', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
    });
  }

  disconnectedCallback() {
    document.removeEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    this.removeEventListener('click', this.completeLookClickHandler);
  }

  /**
   * Handle when the drawer is opened via an event
   * @param {object} evt - Event object.
   */
  handleDrawerOpenViaEvent(evt) {
    this.open(evt.detail ? evt.detail.opener : null);
  }

  /**
   * Trigger refresh of contents
   */
  cartRefreshHandler() {
    this.querySelector('cart-form').refresh();
  }

  /**
   * Update section's cart-form element with new contents
   * @param {string} html - Whole-section HTML.
   */
  updateFromCartChange(html) {
    this.querySelector('cart-form').refreshFromHtml(html);
  }

  /**
   * Best-effort root URL for direct fetches (product JSON, cart update),
   * independent of locale/currency subfolders.
   * @returns {string} - Root URL, always ending in a trailing slash.
   */
  getRootUrl() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    if (theme && theme.routes && theme.routes.root_url) {
      const root = theme.routes.root_url;
      return root.endsWith('/') ? root : `${root}/`;
    }
    return '/';
  }

  /**
   * Handles clicks on "Complete the Look" prompts within cart items.
   * Swaps the current line item — and any of its sibling line items
   * currently present in the cart — for the parent/bundle product,
   * matching the parent's variant to this item's selected options and
   * using this item's quantity.
   * @param {Event} evt - Click event.
   */
  async handleCompleteLookClick(evt) {
    const btn = evt.target.closest('.js-complete-look');
    if (!btn) return;

    evt.preventDefault();

    // Guard against double-clicks and concurrent swaps elsewhere in the drawer.
    if (this.completingLook) return;

    const container = btn.closest('[data-complete-look]');
    if (!container) return;

    this.completingLook = true;
    this.classList.add('is-completing-look');
    container.classList.add('is-loading');
    btn.setAttribute('aria-disabled', 'true');

    const errorEl = container.querySelector('.cart-item__complete-look-error');
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    const itemKey = container.dataset.itemKey;
    const parentHandle = container.dataset.parentHandle;
    const quantity = parseInt(container.dataset.quantity, 10) || 1;
    const siblingKeys = (container.dataset.siblingKeys || '')
      .split(',')
      .map((key) => key.trim())
      .filter((key) => key.length > 0);

    let optionNames = [];
    let variantOptions = [];
    try {
      optionNames = JSON.parse(container.dataset.optionNames || '[]');
      variantOptions = JSON.parse(container.dataset.variantOptions || '[]');
    } catch (e) {
      console.warn('Could not parse option data for Complete the Look', e);
    }

    const rootUrl = this.getRootUrl();
    let addedVariantId = false;

    try {
      if (!parentHandle) throw new Error('Missing parent product.');
      if (!itemKey) throw new Error('Missing line item reference.');

      // 1. Fetch the parent product so we can find the right variant.
      const parentProduct = await fetch(`${rootUrl}products/${parentHandle}.js`, {
        headers: { Accept: 'application/json' }
      }).then((r) => {
        if (!r.ok) throw new Error('Could not load the bundle product.');
        return r.json();
      });

      const targetVariantId = this.resolveParentVariantId(parentProduct, optionNames, variantOptions);
      if (!targetVariantId) throw new Error('The bundle product has no available variants.');

      // 2. Add the parent product first. If this fails, nothing else
      //    has changed and the cart is left exactly as it was.
      const addResponse = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [{ id: targetVariantId, quantity: quantity }]
        })
      });

      const addData = await addResponse.json();

      if (!addResponse.ok || addData.status) {
        const error = typeof addData.description === 'string' ? addData.description : addData.message;
        throw new Error(error || 'Could not add the bundle product to your cart.');
      }

      addedVariantId = targetVariantId;

      // 3. Now remove the original line item and any sibling line items
      //    that are present, in a single update call, requesting fresh
      //    section HTML so the drawer reflects the final state.
      const updates = {};
      updates[itemKey] = 0;
      siblingKeys.forEach((key) => { updates[key] = 0; });

      const sectionsToUpdate = ['page-header', 'cart-drawer']
        .map((sel) => document.querySelector(sel))
        .filter((el) => el);
      const sectionIds = sectionsToUpdate.map((el) => el.dataset.sectionId);

      const updateResponse = await fetch(`${rootUrl}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          updates: updates,
          sections_url: window.location.pathname,
          sections: sectionIds.join(',')
        })
      });

      const updateData = await updateResponse.json();

      if (!updateResponse.ok) {
        // The bundle was added, but we couldn't clean up the original
        // items. Surface this clearly rather than pretending it worked.
        throw new Error('Added the bundle, but could not remove the original items — please remove them manually.');
      }

      if (updateData.sections && sectionsToUpdate.length === Object.keys(updateData.sections).length) {
        sectionsToUpdate.forEach((el) => {
          el.updateFromCartChange(updateData.sections[el.dataset.sectionId]);
        });
      } else {
        this.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
      }

      this.dispatchEvent(new CustomEvent('on:cart:add', {
        bubbles: true,
        detail: { variantId: addedVariantId }
      }));

    } catch (error) {
      console.log(error);
      if (errorEl) {
        errorEl.hidden = false;
        errorEl.textContent = (error && error.message) || 'Something went wrong. Please try again.';
      }
      this.dispatchEvent(new CustomEvent('on:cart:error', {
        bubbles: true,
        detail: { error: (error && error.message) || '' }
      }));
    } finally {
      btn.removeAttribute('aria-disabled');
      container.classList.remove('is-loading');
      this.classList.remove('is-completing-look');
      this.completingLook = false;
    }
  }

  /**
   * Finds the best variant on the parent product to match this line
   * item's currently selected options. Matches by option NAME rather
   * than index, since the parent product's option order/set may not
   * exactly mirror the component product's. Falls back to the parent's
   * first available variant (or its first variant at all) if no exact
   * match can be found.
   * @param {object} parentProduct - Product JSON from /products/{handle}.js.
   * @param {Array} optionNames - This line item's product's option names.
   * @param {Array} variantOptions - This line item's variant's option values,
   *   in the same order as optionNames.
   * @returns {(number|null)} - Matched variant ID, or null if the parent has no variants.
   */
  resolveParentVariantId(parentProduct, optionNames, variantOptions) {
    if (!parentProduct || !Array.isArray(parentProduct.variants) || parentProduct.variants.length === 0) {
      return null;
    }

    const valueByName = {};
    (optionNames || []).forEach((name, i) => {
      if (name) valueByName[String(name).toLowerCase()] = variantOptions[i];
    });

    const parentOptionNames = (parentProduct.options || []).map((opt) => (
      typeof opt === 'string' ? opt : (opt && opt.name)
    ));

    const targetOptions = parentOptionNames.map((name) => {
      const key = (name || '').toLowerCase();
      return Object.prototype.hasOwnProperty.call(valueByName, key) ? valueByName[key] : undefined;
    });

    const hasAnyTarget = targetOptions.some((val) => val !== undefined);

    let match = null;
    if (hasAnyTarget) {
      // Prefer an available variant matching all the options we could map;
      // fall back to any variant matching them if none are available.
      const matches = parentProduct.variants.filter((variant) => (
        variant.options.every((opt, i) => targetOptions[i] === undefined || opt === targetOptions[i])
      ));
      match = matches.find((variant) => variant.available) || matches[0] || null;
    }

    if (match) return match.id;

    // No usable match at all — fall back to the parent's first available
    // variant, or just its first variant.
    const firstAvailable = parentProduct.variants.find((variant) => variant.available);
    return (firstAvailable || parentProduct.variants[0]).id;
  }
}

window.customElements.define('cart-drawer', CartDrawer);